/** @format */

import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { showNotification } from "@mantine/notifications";
import {
  ActionIcon,
  Badge,
  Button,
  Pagination,
  Select,
  Skeleton,
  Table,
  Text,
  TextInput,
  Modal,
} from "@mantine/core";
import { BeatLoader } from "react-spinners";
import { DatePicker } from "@mantine/dates";
import {
  Eye,
  History,
  Plus,
  SortAscending,
  SortDescending,
} from "tabler-icons-react";
import { formatISO } from "date-fns";
import { deleteAgreementBulk } from "../../utilities/apis/agreements";
import {
  loadingStates,
  csvDownload,
  formatDate,
  formatIndianNumber,
} from "../../utilities/utilities";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import {
  getAgreementsList,
  getAgreementsCsvDownload,
} from "../../utilities/apis/agreements";
import PartyNameFilter, {
  PARTY_NAME_LIST_TYPES,
} from "../../components/PartyNameFilter";
import AgreementTypeFilter from "../../components/AgreementTypeFilter";
import { getStatusColor, getStatusText } from "../../utilities/enums";
import colors from "../../utilities/design";
import Styles from "./AgreementsListingPage.module.css";
import { selectUserData } from "../../redux/selectors";
import { useSelector } from "react-redux";
import AgreementAuditLogModal from "./AgreementAuditLogModal";
import RelatedAgreementModal from "./agreementRelatedModal";
import { getBussinessUnit } from "../../utilities/apis/serviceProvider";

// ─── Constants ────────────────────────────────────────────────────────────────

const AGREEMENTS_LIST_TYPE = "agreementsSearchList";

const STATUS_OPTIONS = [
  { label: "CREATED", value: "CREATED" },
  { label: "REVIEW", value: "Review" },
  { label: "APPROVAL", value: "Approval" },
  { label: "FINALISED", value: "Signature" },
  { label: "RE-FINALIZED", value: "RE-SIGNATURE" },
  { label: "PENDING SIGNATURE", value: "SENT" },
  { label: "PARTIALLY COMPLETED", value: "PARTIALLYCOMPLETED" },
  { label: "CANCELLED", value: "CANCELLED" },
  { label: "DECLINED", value: "DECLINED" },
  { label: "SIGNED", value: "SIGNED" },
  { label: "FORCECLOSED", value: "FORCECLOSED" },
];

const ESTAMP_OPTIONS = [
  { label: "Yes", value: "Yes" },
  { label: "No", value: "No" },
];

const SORT_OPTIONS = [
  { label: "Created At", value: "createdAt" },
  { label: "End Date", value: "dueDateOfContract" },
  { label: "Type of Agreement", value: "typeOfAgreement" },
  { label: "Agreement Value", value: "agreementValue" },
  { label: "AIN", value: "id" },
];

const PAGE_SIZE_OPTIONS = ["10", "20", "50"];

const INITIAL_FILTERS = {
  sortBy: "createdAt",
  sortDirection: "desc",
  statusType: null,
  estamp: null,
  agreementType: null,
  location: null,
  startDate: null,
  endDate: null,
  partyName: "",
  partyAddress: "",
  businessunit: null,
};

// ─── Component ────────────────────────────────────────────────────────────────

const AgreementsListPage = () => {
  const navigate = useNavigate();
  const currentUser = useSelector(selectUserData) || {};

  // ── State ──────────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
  const [agreements, setAgreements] = useState([]);
  const [agreementsCount, setAgreementsCount] = useState(0);
  const [businessUnitOptions, setBusinessUnitOptions] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(loadingStates.NO_ACTIVE_REQUEST);
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // ── Modals ─────────────────────────────────────────────────────────────────
  const [auditLogModal, setAuditLogModal] = useState({ open: false, id: null });
  const [relatedModal, setRelatedModal] = useState({ open: false, id: null });
  const [newAgreementModal, setNewAgreementModal] = useState(false);

  // ── Init ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchBusinessUnits();
  }, []);

  // Re-fetch on page, pageSize, sort, or applied filter changes
  useEffect(() => {
    fetchAgreementsList({ page });
  }, [page, pageSize, appliedFilters, filters.sortBy, filters.sortDirection]);

  // ── Data fetchers ──────────────────────────────────────────────────────────
  const fetchBusinessUnits = async () => {
    try {
      const { data } = await getBussinessUnit();
      if (data?.result) {
        setBusinessUnitOptions(
          data.result.map((unit) => ({
            value: String(unit.id),
            label: unit.name,
          })),
        );
      }
    } catch (err) {
      console.error("Error fetching business units:", err);
    }
  };

  // ── Build filter payload ───────────────────────────────────────────────────
  const buildFilterOptions = useCallback((filterState) => {
    if (
      (filterState.startDate && !filterState.endDate) ||
      (!filterState.startDate && filterState.endDate)
    ) {
      showNotification({
        title: "Agreement notification",
        message: "Please select both start and end date",
        color: "red",
      });
      return null;
    }

    const filterOptions = {};

    if (filterState.startDate && filterState.endDate) {
      filterOptions.createdAt = {
        gte: formatISO(filterState.startDate),
        lte: formatISO(filterState.endDate),
      };
    }
    if (filterState.agreementType) {
      filterOptions.typeOfAgreement = { equals: filterState.agreementType };
    }
    if (filterState.statusType) {
      filterOptions.status = { equals: filterState.statusType };
    }
    if (filterState.estamp) {
      filterOptions.agreementType = {
        equals: filterState.estamp === "Yes" ? "eStamp" : "normal",
      };
    }
    if (filterState.businessunit) {
      filterOptions.mapBusinessunit = { equals: filterState.businessunit };
    }
    if (filterState.location) {
      filterOptions.location = filterState.location;
    }
    if (filterState.partyName?.length > 0) {
      filterOptions.OR = [
        {
          secondParty: {
            path: "$.name",
            string_contains: filterState.partyName,
          },
        },
        {
          secondParty: {
            path: "$.indi_name",
            string_contains: filterState.partyName,
          },
        },
        {
          secondParty: {
            path: "$.brandName",
            string_contains: filterState.partyName,
          },
        },
      ];
    }
    if (filterState.partyAddress?.trim()) {
      const term = filterState.partyAddress.trim();
      filterOptions.OR = [
        ...(filterOptions.OR || []),
        { secondParty: { path: "$.address", string_contains: term } },
        { secondParty: { path: "$.corporateAddress", string_contains: term } },
        { secondParty: { path: "$.indi_address", string_contains: term } },
      ];
    }

    return filterOptions;
  }, []);

  const buildListPayload = useCallback(
    (filterState, reqPage) => {
      const filterOptions = buildFilterOptions(filterState);
      if (filterOptions === null) return null;

      return {
        page: reqPage ?? page,
        pageSize,
        sortByOptions: { [filters.sortBy]: filters.sortDirection },
        filterOptions,
        listType: AGREEMENTS_LIST_TYPE,
      };
    },
    [buildFilterOptions, filters.sortBy, filters.sortDirection, page, pageSize],
  );

  // ── Main fetch ─────────────────────────────────────────────────────────────
  const fetchAgreementsList = async ({
    page: reqPage,
    filterState = appliedFilters,
  } = {}) => {
    const payload = buildListPayload(filterState, reqPage);
    if (!payload) return;

    setLoading(loadingStates.LOADING);

    const response = await apiWrapWithErrorWithData(getAgreementsList(payload));

    if (response?.success && response?.agreements) {
      setAgreements(response.agreements);
      setAgreementsCount(response.agreementsCount);
      setLoaded(true);
    }

    setLoading(loadingStates.NO_ACTIVE_REQUEST);
  };

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleFilterChange = (key) => (val) =>
    setFilters((prev) => ({ ...prev, [key]: val }));

  const handleSearch = () => {
    if (buildFilterOptions(filters) === null) return;
    setAppliedFilters({ ...filters });
    setPage(1);
  };

  const handleClear = () => {
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    setPage(1);
  };

  const handleExportCsv = async () => {
    const exportPayload = buildListPayload(appliedFilters, 1);
    if (!exportPayload) return;

    try {
      setExportLoading(true);
      const response = await apiWrapWithErrorWithData(
        getAgreementsCsvDownload({
          page: exportPayload.page,
          sortByOptions: exportPayload.sortByOptions,
          filterOptions: exportPayload.filterOptions,
          listType: AGREEMENTS_LIST_TYPE,
        }),
      );
      if (response?.success && response?.agreements?.length > 0) {
        csvDownload({
          data: response.agreements,
          headers: Object.keys(response.agreements[0]),
          name: "my agreements",
        });
      } else {
        showNotification({
          title: "Export",
          message: "No agreements available",
          color: "red",
        });
      }
    } catch {
      showNotification({
        title: "Export",
        message: "Could not export CSV",
        color: "red",
      });
    } finally {
      setExportLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    const response = await apiWrapWithErrorWithData(
      deleteAgreementBulk({ agreementIds: selectedIds }),
    );
    if (response?.success) {
      showNotification({
        color: "green",
        title: "Agreements",
        message: "Deleted successfully.",
      });
      navigate("/app/agreements_manager");
    } else {
      showNotification({
        color: "red",
        title: "Agreements",
        message: "Unable to delete.",
      });
    }
    setDeleteLoading(false);
  };
  const isAdmin =
    currentUser?.loginAs?.id === 1 || currentUser?.loginAs?.id === 2;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div className={Styles.pageHeader}>
        <h2>Agreements List</h2>
        <Text className={Styles.pageSubtitle}>
          Search and manage your agreements
        </Text>
      </div>

      <div className={Styles.filterPanel}>
        <div className="grid grid-cols-4 gap-3">
          <Select
            placeholder="Agreement status"
            value={filters.statusType}
            onChange={handleFilterChange("statusType")}
            data={STATUS_OPTIONS}
            clearable
          />
          <Select
            placeholder="eStamp"
            value={filters.estamp}
            onChange={handleFilterChange("estamp")}
            data={ESTAMP_OPTIONS}
            clearable
          />
          <AgreementTypeFilter
            value={filters.agreementType}
            onChange={handleFilterChange("agreementType")}
          />
          <Select
            placeholder="Business unit"
            value={filters.businessunit}
            data={businessUnitOptions}
            onChange={handleFilterChange("businessunit")}
            clearable
          />
          <DatePicker
            placeholder="Created date (from)"
            value={filters.startDate}
            onChange={handleFilterChange("startDate")}
            minDate={new Date(new Date().setMonth(new Date().getMonth() - 3))}
          />
          <DatePicker
            placeholder="Created date (to)"
            value={filters.endDate}
            onChange={handleFilterChange("endDate")}
            minDate={filters.startDate}
            disabled={!filters.startDate}
          />
          <PartyNameFilter
            value={filters.partyName}
            onChange={(val) =>
              setFilters((prev) => ({ ...prev, partyName: val }))
            }
            listType={PARTY_NAME_LIST_TYPES.AGREEMENTS}
          />
          <TextInput
            placeholder="State / City"
            value={filters.partyAddress}
            onChange={(e) => {
              const value = e.target.value;
              setFilters((prev) => ({ ...prev, partyAddress: value }));
            }}
          />
        </div>
      </div>

      <div
        className={`flex items-center justify-between my-4 ${Styles.actionsBar}`}
      >
        <div className="flex gap-2 flex-wrap">
          <Button color="green" onClick={handleSearch}>
            Search
          </Button>
          <Button color="red" onClick={handleClear}>
            Clear
          </Button>
          {isAdmin && (
            <Button
              disabled={deleteLoading || selectedIds.length === 0}
              style={{
                backgroundColor: deleteLoading ? "#ffcccc" : "#ff4d4d",
                color: "#F5F5F5",
              }}
              onClick={handleDelete}
            >
              {deleteLoading ? (
                <BeatLoader size={10} color={colors.rejected} />
              ) : (
                "Delete"
              )}
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Select
            value={filters.sortBy}
            onChange={handleFilterChange("sortBy")}
            data={SORT_OPTIONS}
          />
          <ActionIcon
            onClick={() =>
              setFilters((prev) => ({
                ...prev,
                sortDirection: prev.sortDirection === "asc" ? "desc" : "asc",
              }))
            }
          >
            {filters.sortDirection === "asc" ? (
              <SortAscending size={32} />
            ) : (
              <SortDescending size={32} />
            )}
          </ActionIcon>
          <Button
            disabled={exportLoading}
            onClick={handleExportCsv}
            style={{ backgroundColor: "#46BDE1" }}
          >
            Export
          </Button>
        </div>
      </div>

      {loading === loadingStates.LOADING && (
        <div className="flex flex-col mt-4 gap-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} height={30} radius="md" className="w-full" />
          ))}
          <div className="flex justify-center pt-2">
            <Skeleton height={40} radius="md" className="w-40" />
          </div>
        </div>
      )}

      {loaded && loading !== loadingStates.LOADING && agreements.length > 0 && (
        <div
          className={`${Styles.tableScrollWrapper} ${Styles.agreementListTable}`}
        >
          <Table striped>
            <thead>
              <tr>
                <th>AR NO.</th>
                <th>Type</th>
                <th>Business Unit</th>
                <th>Status</th>
                <th>Second Party</th>
                <th>Contract Date</th>
                <th>End Date</th>
                {/* <th>TN Period</th> */}
                <th colSpan={3}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {agreements
                .filter((v) => v.agreementData?.json?.typeOfAgreement)
                .map((row) => (
                  <tr key={row.id}>
                    <td>{`AR-${row.id}`}</td>
                    <td>{row.agreementData.json.typeOfAgreement}</td>
                    <td>{row.agreementData?.json?.location || "—"}</td>
                    <td>
                      <Badge color={getStatusColor(row.status)}>
                        {getStatusText(row.status)}
                      </Badge>
                    </td>
                    <td>
                      {row.agreementData.json.brandName}
                      <br />
                      <span className="font-light">
                        {`(${row.agreementData?.json?.secondParty?.name || "-"})`}
                      </span>
                    </td>
                    <td>{formatDate(row.createdAt)}</td>
                    <td>{formatDate(row.dueDateOfContract)}</td>
                    {/* <td>{row.terminationNoticePeriod ?? "—"}</td> */}
                    <td style={{ textAlign: "center", padding: "10px 2px" }}>
                      {row.relationshipWith ? (
                        <ActionIcon
                          onClick={() =>
                            setRelatedModal({ open: true, id: row.id })
                          }
                          color="white"
                        >
                          <Plus color="#46BDE1" size={24} />
                        </ActionIcon>
                      ) : (
                        <span>—</span>
                      )}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <div className="flex flex-row gap-2">
                        <Link
                          to={`/app/agreements_manager/agreements/details/${row.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Eye color="#46BDE1" size={24} />
                        </Link>
                        <Link
                          to={`/app/agreements_manager/agreements/audit-log/${row.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <History color="#46BDE1" size={24} />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      )}

      {loaded &&
        loading !== loadingStates.LOADING &&
        agreements.length === 0 && (
          <div className={Styles.emptyState}>No agreements found</div>
        )}

      {agreementsCount > 0 && (
        <div className="flex flex-row justify-center items-center my-4 gap-3">
          <Pagination
            page={page}
            onChange={setPage}
            total={Math.ceil(agreementsCount / pageSize)}
          />
          <Text>Page size</Text>
          <Select
            className="w-20"
            data={PAGE_SIZE_OPTIONS}
            value={String(pageSize)}
            onChange={(val) => {
              setPageSize(parseInt(val));
              setPage(1);
            }}
          />
        </div>
      )}

      {/* ── New Agreement Modal ───────────────────────────────────────────── */}
      <Modal
        opened={newAgreementModal}
        onClose={() => setNewAgreementModal(false)}
        size="calc(40vw)"
        style={{ marginTop: "12%" }}
        title="Select Agreement Type"
      >
        <div className={Styles["popup-desig"]}>
          <p
            style={{
              cursor: "pointer",
              border: "2px solid lightblue",
              padding: 8,
              marginBottom: 8,
            }}
            onClick={() => navigate("/app/agreements_manager/agreements/new")}
          >
            Individual Agreement
          </p>
          <p
            style={{
              cursor: "pointer",
              border: "2px solid lightblue",
              padding: 8,
            }}
            onClick={() =>
              navigate("/app/agreements_manager/agreements/new-bulk")
            }
          >
            Bulk Agreement
          </p>
          <div style={{ textAlign: "center", paddingTop: "3%" }}>
            <button
              style={{
                cursor: "pointer",
                background: "red",
                border: "none",
                padding: 8,
                color: "white",
                borderRadius: "10%",
              }}
              onClick={() => setNewAgreementModal(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Audit Log Modal ───────────────────────────────────────────────── */}
      {auditLogModal.open && (
        <Modal
          overflow="inside"
          opened
          onClose={() => setAuditLogModal({ open: false, id: null })}
          size="calc(80vw)"
        >
          <AgreementAuditLogModal agreementId={auditLogModal.id} />
        </Modal>
      )}

      {/* ── Related Agreement Modal ───────────────────────────────────────── */}
      {relatedModal.open && (
        <Modal
          overflow="inside"
          opened
          onClose={() => setRelatedModal({ open: false, id: null })}
          size="calc(80vw)"
        >
          <RelatedAgreementModal agreementId={relatedModal.id} />
        </Modal>
      )}
    </>
  );
};

export default AgreementsListPage;
